import React, { useState } from 'react';
import { XMarkIcon, PencilSquareIcon, GlobeAltIcon, EyeSlashIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Gallery } from '../../types/gallery';
import { formatDate } from '../../utils/dateHelpers';
import Modal from '../common/Modal';

interface GalleryDetailsProps {
  gallery: Gallery;
  onClose: () => void;
  onEdit: () => void;
}

const GalleryDetails: React.FC<GalleryDetailsProps> = ({ gallery, onClose, onEdit }) => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={gallery.title}
      size="xl"
    >
      <div className="p-6 space-y-6">
        {/* Header Info */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {gallery.description && (
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {gallery.description}
              </p>
            )}
            
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <CalendarIcon className="w-4 h-4" />
                <span>Létrehozva: {formatDate(gallery.createdAt)}</span>
              </div>
              
              <div className="flex items-center gap-1">
                {gallery.isPublished ? (
                  <>
                    <GlobeAltIcon className="w-4 h-4 text-green-500" />
                    <span className="text-green-600 dark:text-green-400">Publikált</span>
                  </>
                ) : (
                  <>
                    <EyeSlashIcon className="w-4 h-4 text-gray-500" />
                    <span>Piszkozat</span>
                  </>
                )}
              </div>
              
              <div>
                <span className="font-medium">{gallery.images.length}</span> kép
              </div>
            </div>

            {gallery.slug && (
              <div className="mt-2 text-sm">
                <span className="text-gray-500 dark:text-gray-400">URL slug:</span>
                <code className="ml-2 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-primary-600 dark:text-primary-400">
                  {gallery.slug}
                </code>
              </div>
            )}
          </div>

          <button
            onClick={onEdit}
            className="ml-4 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <PencilSquareIcon className="w-4 h-4" />
            Szerkesztés
          </button>
        </div>

        {/* Image Gallery */}
        {gallery.images.length > 0 ? (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Képek
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.images
                .sort((a, b) => a.order - b.order)
                .map((image, index) => (
                  <div
                    key={image.id}
                    className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all"
                    onClick={() => setSelectedImage(index)}
                  >
                    <img
                      src={image.url}
                      alt={image.title || `Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm">
                        Megtekintés
                      </span>
                    </div>

                    {/* Order badge */}
                    <div className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                      #{index + 1}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Nincs kép ebben a galériában
          </div>
        )}

        {/* Image Lightbox */}
        {selectedImage !== null && (
          <div
            className="fixed inset-0 z-[100] bg-black bg-opacity-90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="w-8 h-8" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(prev => 
                  prev! > 0 ? prev! - 1 : gallery.images.length - 1
                );
              }}
              className="absolute left-4 text-white hover:text-gray-300 transition-colors text-4xl"
            >
              ‹
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(prev => 
                  prev! < gallery.images.length - 1 ? prev! + 1 : 0
                );
              }}
              className="absolute right-4 text-white hover:text-gray-300 transition-colors text-4xl"
            >
              ›
            </button>

            <div className="max-w-5xl max-h-[90vh] flex flex-col items-center">
              <img
                src={gallery.images[selectedImage].url}
                alt={gallery.images[selectedImage].title || ''}
                className="max-w-full max-h-[80vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              
              {(gallery.images[selectedImage].title || gallery.images[selectedImage].description) && (
                <div className="mt-4 text-center text-white max-w-2xl">
                  {gallery.images[selectedImage].title && (
                    <h4 className="text-lg font-semibold mb-1">
                      {gallery.images[selectedImage].title}
                    </h4>
                  )}
                  {gallery.images[selectedImage].description && (
                    <p className="text-sm text-gray-300">
                      {gallery.images[selectedImage].description}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-2 text-white text-sm">
                {selectedImage + 1} / {gallery.images.length}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GalleryDetails;
